export type ImageLoadPriority = "foreground" | "background";

type LoadTask<T> = {
  key: string;
  priority: ImageLoadPriority;
  run: (signal: AbortSignal) => Promise<T>;
  resolve: (value: T) => void;
  reject: (reason?: unknown) => void;
  promise: Promise<T>;
  controller?: AbortController;
};

/**
 * Coordinates annotation image downloads without allowing thumbnails to block
 * the image the user is actively trying to annotate.
 */
export class ImageLoadManager {
  private static foregroundQueue: LoadTask<any>[] = [];
  private static backgroundQueue: LoadTask<any>[] = [];
  private static tasks = new Map<string, LoadTask<any>>();
  private static activeForeground = 0;
  private static activeBackground = 0;
  private static generation = 0;
  private static readonly backgroundConcurrency = 3;

  public static load<T>(
    key: string,
    run: (signal: AbortSignal) => Promise<T>,
    priority: ImageLoadPriority = "background"
  ): Promise<T> {
    const existing = ImageLoadManager.tasks.get(key) as LoadTask<T> | undefined;
    if (existing) {
      if (priority === "foreground" && existing.priority === "background" && !existing.controller) {
        ImageLoadManager.backgroundQueue = ImageLoadManager.backgroundQueue.filter(
          (task) => task !== existing
        );
        existing.priority = "foreground";
        ImageLoadManager.foregroundQueue.push(existing);
        ImageLoadManager.pump();
      }
      return existing.promise;
    }

    let resolveTask!: (value: T) => void;
    let rejectTask!: (reason?: unknown) => void;
    const promise = new Promise<T>((resolve, reject) => {
      resolveTask = resolve;
      rejectTask = reject;
    });
    const task: LoadTask<T> = {
      key,
      priority,
      run,
      resolve: resolveTask,
      reject: rejectTask,
      promise,
    };

    ImageLoadManager.tasks.set(key, task);
    const queue = priority === "foreground"
      ? ImageLoadManager.foregroundQueue
      : ImageLoadManager.backgroundQueue;
    queue.push(task);
    ImageLoadManager.pump();
    return promise;
  }

  public static reset(): void {
    ImageLoadManager.generation += 1;
    const abortError = new DOMException("Image loading was reset", "AbortError");
    [...ImageLoadManager.foregroundQueue, ...ImageLoadManager.backgroundQueue].forEach(
      (task) => task.reject(abortError)
    );
    ImageLoadManager.tasks.forEach((task) => task.controller?.abort());
    ImageLoadManager.foregroundQueue = [];
    ImageLoadManager.backgroundQueue = [];
    ImageLoadManager.tasks.clear();
    ImageLoadManager.activeForeground = 0;
    ImageLoadManager.activeBackground = 0;
  }

  private static pump(): void {
    while (
      ImageLoadManager.activeForeground < 1 &&
      ImageLoadManager.foregroundQueue.length > 0
    ) {
      const task = ImageLoadManager.foregroundQueue.shift();
      if (task) ImageLoadManager.start(task);
    }

    while (
      ImageLoadManager.activeBackground < ImageLoadManager.backgroundConcurrency &&
      ImageLoadManager.backgroundQueue.length > 0
    ) {
      const task = ImageLoadManager.backgroundQueue.shift();
      if (task) ImageLoadManager.start(task);
    }
  }

  private static start<T>(task: LoadTask<T>): void {
    const generation = ImageLoadManager.generation;
    const isForeground = task.priority === "foreground";
    if (isForeground) ImageLoadManager.activeForeground += 1;
    else ImageLoadManager.activeBackground += 1;

    task.controller = new AbortController();
    task.run(task.controller.signal)
      .then(task.resolve)
      .catch(task.reject)
      .finally(() => {
        if (generation !== ImageLoadManager.generation) return;
        if (isForeground) ImageLoadManager.activeForeground -= 1;
        else ImageLoadManager.activeBackground -= 1;
        ImageLoadManager.tasks.delete(task.key);
        ImageLoadManager.pump();
      });
  }
}
